
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/public/form/submit/newsletter-cajas-carton', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('¡Gracias por suscribirte!');
        form.reset();
        setTimeout(() => setFormStatus(''), 3000);
      } else {
        setFormStatus('Error al enviar. Intenta de nuevo.');
      }
    } catch (error) {
      setFormStatus('Error al enviar. Intenta de nuevo.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/10 backdrop-blur-md'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/9d7e00fa19ae08e567bf43793edea725/46849956b79c85a45fff15dea5133c59.jpeg" 
                alt="Logo Cajas de Cartón" 
                className="h-12 w-12 object-contain"
              />
              <span className={`font-bold text-lg ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                Cajas Premium
              </span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#inicio" className={`font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-white/80'}`}>
                Inicio
              </a>
              <a href="#productos" className={`font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-white/80'}`}>
                Productos
              </a>
              <a href="#personalizacion" className={`font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-white/80'}`}>
                Personalización
              </a>
              <a href="#contacto" className={`font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-white/80'}`}>
                Contacto
              </a>
              <a href="#sobre-nosotros" className={`font-medium transition-colors ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-white/80'}`}>
                Sobre Nosotros
              </a>
            </div>

            <a 
              href="https://wa.me/524771234567" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-[#25D366] text-white px-5 py-2.5 rounded-full font-medium hover:bg-[#20BD5A] transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              Atención Personalizada
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="inicio" className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/d0a39a497a12ee4bf858d6ba0ed94bdc.jpg" 
            alt="Almacén de cajas de cartón"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-3xl">
            <div className="inline-block mb-6 px-4 py-2 border-2 border-white/80 rounded-full">
              <span className="text-white font-medium text-sm">Desde 600 cajas - Pedido mínimo</span>
            </div>
            
            <h1 className="text-6xl md:text-7xl font-bold text-white mb-4 leading-tight">
              Cajas de Cartón<br />
              <span className="font-extrabold">A Tu Medida</span>
            </h1>
            
            <p className="text-xl text-white/90 mb-8 font-light">
              Fabricación personalizada | Moroleón, Guanajuato
            </p>

            <div className="flex flex-wrap gap-4">
              <a 
                href="#productos" 
                className="bg-[#F97316] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#EA580C] transition-colors cursor-pointer whitespace-nowrap"
              >
                Ver Productos
              </a>
              <a 
                href="#contacto" 
                className="bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap"
              >
                Solicitar Cotización
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="text-5xl font-bold text-gray-900 mb-4">¿Por Qué Elegirnos?</h2>
            </div>
            <div className="flex items-center">
              <p className="text-xl text-gray-600 font-light">
                Calidad industrial con servicio personalizado para tu negocio
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-[#FEF3E2] rounded-3xl p-8 text-center">
              <div className="w-16 h-16 bg-[#F97316] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-ruler-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Medidas Personalizadas</h3>
              <p className="text-gray-600 leading-relaxed">
                Especifica las dimensiones exactas que necesitas. Nos adaptamos a tu producto.
              </p>
            </div>

            <div className="bg-[#F0F9FF] rounded-3xl p-8 text-center">
              <div className="w-16 h-16 bg-[#0EA5E9] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-stack-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Sencillas y Reforzadas</h3>
              <p className="text-gray-600 leading-relaxed">
                Elige el tipo de cartón según tus necesidades de resistencia y protección.
              </p>
            </div>

            <div className="bg-[#ECFDF5] rounded-3xl p-8 text-center">
              <div className="w-16 h-16 bg-[#10B981] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-box-3-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Desde 600 Unidades</h3>
              <p className="text-gray-600 leading-relaxed">
                Pedidos al mayoreo con precios competitivos para tu negocio.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="productos" className="py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
              <span className="text-sm font-semibold text-gray-600 tracking-wider uppercase">Catálogo</span>
            </div>
            <h2 className="text-5xl font-serif font-bold text-gray-900">
              Tamaños Estándar<br />
              <span className="italic font-light">y Personalizados</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow">
              <div className="aspect-square bg-[#FFF1F2] flex items-center justify-center p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=single%20brown%20corrugated%20cardboard%20box%20small%20size%20clean%20simple%20background%20product%20photography%20centered%20composition%20minimal%20style%20industrial%20packaging&width=400&height=400&seq=box-small-001&orientation=squarish" 
                  alt="Caja pequeña"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">30 × 20 × 20 cm</h3>
                <p className="text-gray-600 mb-3">Sencilla</p>
                <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                  Desde 600 pzas
                </span>
              </div>
            </div>

            <div className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow">
              <div className="aspect-square bg-[#DCFCE7] flex items-center justify-center p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=medium%20brown%20corrugated%20cardboard%20box%20clean%20simple%20background%20product%20photography%20centered%20composition%20minimal%20style%20industrial%20packaging%20double%20wall%20reinforced&width=400&height=400&seq=box-medium-002&orientation=squarish" 
                  alt="Caja mediana"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">40 × 30 × 30 cm</h3>
                <p className="text-gray-600 mb-3">Reforzada</p>
                <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                  Desde 600 pzas
                </span>
              </div>
            </div>

            <div className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow">
              <div className="aspect-square bg-[#F3F4F6] flex items-center justify-center p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=large%20brown%20corrugated%20cardboard%20box%20clean%20simple%20background%20product%20photography%20centered%20composition%20minimal%20style%20industrial%20packaging%20sturdy%20construction&width=400&height=400&seq=box-large-003&orientation=squarish" 
                  alt="Caja grande"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">50 × 40 × 40 cm</h3>
                <p className="text-gray-600 mb-3">Sencilla</p>
                <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                  Desde 600 pzas
                </span>
              </div>
            </div>

            <div className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow">
              <div className="aspect-square bg-[#FEF9C3] flex items-center justify-center p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=extra%20large%20brown%20corrugated%20cardboard%20box%20clean%20simple%20background%20product%20photography%20centered%20composition%20minimal%20style%20industrial%20packaging%20heavy%20duty%20reinforced&width=400&height=400&seq=box-xlarge-004&orientation=squarish" 
                  alt="Caja extra grande"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">60 × 50 × 50 cm</h3>
                <p className="text-gray-600 mb-3">Reforzada</p>
                <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                  Desde 600 pzas
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customization CTA Section */}
      <section id="personalizacion" className="grid md:grid-cols-2 min-h-[600px]">
        <div className="relative overflow-hidden">
          <img 
            src="https://readdy.ai/api/search-image?query=cardboard%20box%20manufacturing%20process%20industrial%20facility%20workers%20assembling%20corrugated%20boxes%20production%20line%20machinery%20clean%20organized%20workspace%20quality%20control%20packaging%20factory%20floor&width=960&height=600&seq=manufacturing-process-001&orientation=landscape" 
            alt="Proceso de fabricación"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#F97316]/30"></div>
          <div className="absolute bottom-12 left-12">
            <span className="text-white text-sm font-semibold tracking-wider uppercase mb-3 block">
              Pedidos Personalizados
            </span>
            <h2 className="text-5xl font-bold text-white leading-tight">
              Cotiza Tu<br />
              Pedido Hoy<br />
              Mismo
            </h2>
          </div>
        </div>

        <div className="bg-white flex items-center justify-center p-12 md:p-16">
          <div className="max-w-md">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Atención Directa por WhatsApp
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Nuestro equipo está listo para asesorarte. Envíanos las medidas que necesitas y te respondemos con cotización inmediata. Servicio personalizado para cada cliente.
            </p>
            <a 
              href="https://wa.me/524771234567?text=Hola,%20me%20interesa%20cotizar%20cajas%20de%20cartón" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#20BD5A] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-2xl"></i>
              Iniciar Conversación
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-12 items-center">
            <div className="md:col-span-3">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
                <span className="text-sm font-semibold text-gray-600 tracking-wider uppercase">
                  Clientes Satisfechos
                </span>
              </div>
              
              <div className="mb-8">
                <i className="ri-double-quotes-l text-6xl text-gray-200"></i>
              </div>

              <blockquote className="text-4xl font-serif text-gray-900 leading-snug mb-8">
                Las cajas llegaron perfectas y en el tiempo prometido. Excelente calidad.
              </blockquote>

              <div className="flex items-center gap-4">
                <div>
                  <p className="font-bold text-gray-900 text-lg">Roberto Martínez</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#10B981] text-white text-sm rounded-full">
                      <i className="ri-star-fill text-xs"></i>
                      Cliente Verificado
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-2">
              <img 
                src="https://readdy.ai/api/search-image?query=satisfied%20business%20owner%20standing%20next%20to%20stacked%20cardboard%20boxes%20in%20warehouse%20professional%20portrait%20black%20and%20white%20photography%20confident%20entrepreneur%20industrial%20setting%20quality%20packaging&width=600&height=800&seq=client-testimonial-001&orientation=portrait" 
                alt="Cliente satisfecho"
                className="w-full h-[500px] object-cover rounded-3xl grayscale"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="sobre-nosotros" className="py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
                <span className="text-sm font-semibold text-gray-600 tracking-wider uppercase">
                  Sobre Nosotros
                </span>
              </div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Calidad y Experiencia en Moroleón
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Somos una empresa dedicada a la fabricación de cajas de cartón con años de experiencia en el mercado. Ubicados en Moroleón, Guanajuato, ofrecemos soluciones de empaque personalizadas para todo tipo de negocios.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Nuestro compromiso es brindar productos de alta calidad a precios competitivos, con atención personalizada que garantiza la satisfacción de cada cliente. Trabajamos con pedidos desde 600 unidades, adaptándonos a las necesidades específicas de tu negocio.
              </p>
              
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <div className="text-4xl font-bold text-[#F97316] mb-2">10+</div>
                  <p className="text-gray-600 text-sm">Años de Experiencia</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-[#F97316] mb-2">500+</div>
                  <p className="text-gray-600 text-sm">Clientes Satisfechos</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-[#F97316] mb-2">100%</div>
                  <p className="text-gray-600 text-sm">Calidad Garantizada</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=modern%20cardboard%20box%20factory%20interior%20clean%20organized%20production%20facility%20manufacturing%20equipment%20corrugated%20boxes%20stacked%20professionally%20industrial%20workspace%20quality%20control%20area%20bright%20lighting&width=800&height=600&seq=factory-interior-001&orientation=landscape" 
                alt="Nuestras instalaciones"
                className="w-full h-[600px] object-cover rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-8 -left-8 bg-white p-8 rounded-2xl shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-[#F97316] rounded-xl flex items-center justify-center">
                    <i className="ri-map-pin-line text-3xl text-white"></i>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Moroleón, Guanajuato</p>
                    <p className="text-gray-600 text-sm">Visítanos</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Contáctanos</h2>
            <p className="text-xl text-gray-600">Estamos listos para atender tu pedido</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <a 
              href="https://wa.me/524771234567" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-[#FAF8F5] rounded-2xl p-8 text-center hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="w-16 h-16 bg-[#25D366] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-whatsapp-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">WhatsApp</h3>
              <p className="text-gray-600">Atención inmediata</p>
            </a>

            <a 
              href="tel:+524771234567" 
              className="bg-[#FAF8F5] rounded-2xl p-8 text-center hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="w-16 h-16 bg-[#0EA5E9] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Teléfono</h3>
              <p className="text-gray-600">477 123 4567</p>
            </a>

            <div className="bg-[#FAF8F5] rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-[#F97316] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Ubicación</h3>
              <p className="text-gray-600">Moroleón, Guanajuato</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2A2A2A] text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-between items-start mb-12">
            <div className="mb-8 md:mb-0">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://static.readdy.ai/image/9d7e00fa19ae08e567bf43793edea725/46849956b79c85a45fff15dea5133c59.jpeg" 
                  alt="Logo" 
                  className="h-10 w-10 object-contain brightness-0 invert"
                />
                <span className="font-bold text-xl">Cajas Premium</span>
              </div>
              <p className="text-gray-400 max-w-xs">
                Fabricantes de cajas de cartón en Moroleón, Guanajuato
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <h4 className="font-bold text-lg mb-4">Productos</h4>
              <ul className="space-y-2">
                <li><a href="#productos" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Cajas Sencillas</a></li>
                <li><a href="#productos" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Cajas Reforzadas</a></li>
                <li><a href="#productos" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Medidas Estándar</a></li>
                <li><a href="#personalizacion" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Pedidos Personalizados</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Empresa</h4>
              <ul className="space-y-2">
                <li><a href="#sobre-nosotros" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Sobre Nosotros</a></li>
                <li><a href="#sobre-nosotros" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Calidad</a></li>
                <li><a href="#contacto" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Ubicación</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Soporte</h4>
              <ul className="space-y-2">
                <li><a href="#contacto" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Preguntas Frecuentes</a></li>
                <li><a href="#contacto" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Cotizaciones</a></li>
                <li><a href="#contacto" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Tiempos de Entrega</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Contacto</h4>
              <div className="flex gap-4 mb-4">
                <a 
                  href="https://wa.me/524771234567" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                </a>
                <a 
                  href="https://facebook.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a 
                  href="https://instagram.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <i className="ri-instagram-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-center py-8 border-t border-white/10">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=cardboard%20boxes%20in%20real%20use%20shipping%20packages%20stacked%20ready%20for%20delivery%20e-commerce%20packaging%20business%20supplies%20clean%20organized%20storage&width=300&height=200&seq=boxes-in-use-001&orientation=landscape" 
                alt="Cajas en uso"
                className="w-full h-32 object-cover rounded-2xl"
              />
            </div>

            <div className="text-center">
              <h3 className="text-4xl font-serif font-bold leading-tight">
                Calidad<br />
                Garantizada<br />
                Siempre
              </h3>
            </div>

            <div>
              <p className="text-gray-400 text-sm mb-4">
                Recibe ofertas especiales y novedades directo en tu correo
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2" data-readdy-form id="newsletter-form">
                <input 
                  type="email" 
                  name="email"
                  placeholder="tu@email.com"
                  required
                  className="flex-1 px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-white/40 text-sm"
                />
                <button 
                  type="submit"
                  className="bg-[#0EA5E9] text-white px-6 py-2.5 rounded-lg font-medium hover:bg-[#0284C7] transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Suscribir →
                </button>
              </form>
              {formStatus && (
                <p className="text-sm mt-2 text-[#10B981]">{formStatus}</p>
              )}
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 flex flex-wrap justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © 2025 Cajas Premium. Todos los derechos reservados.
            </p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
